/**
 * Sticky banner surfacing trial + payment-failure state for the org.
 *
 * Two trigger conditions:
 *   • ``subscription_status === "trialing"`` AND the trial ends within
 *     7 days → soft nudge with the remaining day count and an Upgrade
 *     CTA that routes to ``/settings/billing``.
 *   • ``subscription_status === "past_due"`` → hard warning asking the
 *     user to update their payment method.
 *
 * Mounted once in :file:`AppShell.tsx` so every authenticated page shows
 * the same banner. Non-admins see the status but the CTA is disabled
 * unless they hold ``manage_billing``.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles } from "lucide-react";

import { getSubscription, type SubscriptionState } from "@/api/billing";
import { useAuth } from "@/hooks/useAuth";
import { useDemoSession } from "@/hooks/useDemoSession";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { cn } from "@/lib/utils";

const TRIAL_WARNING_DAYS = 7;

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export function TrialBanner() {
  const { isAuthenticated } = useAuth();
  const { isDemo } = useDemoSession();
  const { can, hasRole } = usePermissions();
  const { features } = usePlanFeatures();
  // Internal/founder orgs (Lenavio team) bypass the entire billing
  // gate. Suppress the banner unconditionally so we never see
  // "your trial ends" copy on our own tenant.
  const isInternalOrg = features?.is_internal === true;

  // Audience gate. Trial / billing nag is operational noise for
  // anyone who can't act on it — employees in particular shouldn't
  // see "your trial ends" copy because (a) they're not the
  // buyer and (b) it implies their access is about to disappear,
  // which is anxiety-inducing for no reason. Scoped strictly to
  // SUPER_ADMIN — they're the buyer of record and the only role
  // that can act on the upgrade CTA anyway (manage_billing is
  // SUPER_ADMIN-only in ROLE_PERMISSIONS).
  const audienceAllowed = hasRole("SUPER_ADMIN");

  const [state, setState] = useState<SubscriptionState | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? window.localStorage.getItem("trialBannerDismissed")
      : null
  );

  // Fire-and-forget fetch. A failure here shouldn't block the app — the
  // banner simply stays hidden. We intentionally don't refetch on route
  // changes; webhook-driven status updates are eventually consistent and
  // the page will reflect them on the next reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    // Demo sessions are capped server-side; showing an upgrade banner
    // on top of the demo banner would be visual noise.
    if (isDemo) return;
    // Skip the API call entirely for audiences who can't see the
    // banner — saves a request per page-load for every employee.
    if (!audienceAllowed) return;
    // Internal/founder orgs are exempt — never fetch billing state.
    if (isInternalOrg) return;
    let cancelled = false;
    getSubscription()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch(() => {
        /* ignore — banner stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isDemo, audienceAllowed, isInternalOrg]);

  // Defense in depth — if the audience flag flips after the fetch
  // (role change, impersonation, etc.) we still hide the banner.
  if (!audienceAllowed) return null;
  // Hard suppress for internal orgs even if a stale state was loaded
  // before is_internal arrived from the features endpoint.
  if (isInternalOrg) return null;

  if (!state) return null;

  const status = state.subscription_status;
  const isPastDue = status === "past_due";
  const isTrialing = status === "trialing";
  // Trial elapsed without a paid plan. The full-screen lockout (see
  // TrialExpiredLockout) already handles the in-product blocker, but
  // we surface the banner too on the /settings/billing path so the
  // CTA is one tap away even before they've scrolled.
  const isTrialExpired = status === "trial_expired";
  const trialDays = isTrialing ? daysUntil(state.trial_ends_at) : null;
  const showTrial =
    isTrialing && trialDays !== null && trialDays <= TRIAL_WARNING_DAYS;

  if (!isPastDue && !showTrial && !isTrialExpired) return null;

  // Dismissal is scoped to (status, day-count) so a fresh past-due event
  // or a new day on the trial-countdown re-surfaces the banner.
  const bannerKey = isPastDue
    ? "past_due"
    : isTrialExpired
      ? "trial_expired"
      : `trial:${trialDays}`;
  if (dismissedKey === bannerKey) return null;

  const canManage = can("manage_billing");

  const onDismiss = () => {
    setDismissedKey(bannerKey);
    try {
      window.localStorage.setItem("trialBannerDismissed", bannerKey);
    } catch {
      /* ignore quota errors */
    }
  };

  const tone =
    isPastDue || isTrialExpired
      ? "bg-danger/10 border-danger/40 text-danger"
      : "bg-warning/10 border-warning/40 text-warning-foreground";

  return (
    <div
      role="status"
      className={cn(
        "mb-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-3 text-sm",
        tone
      )}
    >
      <div className="flex items-center gap-2">
        {isPastDue || isTrialExpired ? (
          <AlertTriangle className="h-4 w-4 shrink-0" />
        ) : (
          <Sparkles className="h-4 w-4 shrink-0" />
        )}
        <span className="text-text-primary">
          {isPastDue ? (
            <>
              <strong>Payment failed.</strong> Update your payment method to
              avoid losing access.
            </>
          ) : isTrialExpired ? (
            <>
              <strong>Your trial has ended.</strong> Pick a plan to restore
              full access.
            </>
          ) : trialDays === 0 ? (
            <>
              <strong>Your trial ends today.</strong> Pick a plan to keep
              access.
            </>
          ) : trialDays === 1 ? (
            <>
              <strong>Your trial ends tomorrow.</strong> Pick a plan to keep
              access.
            </>
          ) : (
            <>
              <strong>{trialDays} days left in your trial.</strong>{" "}
              Pick a plan any time to lock in the founding rate.
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {canManage && (
          <Link
            to="/settings/billing"
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              isPastDue || isTrialExpired
                ? "bg-danger text-white hover:bg-danger/90"
                : "bg-accent text-white hover:bg-accent/90"
            )}
          >
            {isPastDue
              ? "Fix payment"
              : isTrialExpired
                ? "Choose a plan"
                : "Upgrade"}
          </Link>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="text-xs text-text-muted hover:text-text-primary"
          aria-label="Dismiss banner"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
